// Post creator: details, structured frame composition, and the real post viewer.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import firebase from "firebase/app";
import "firebase/firestore";
import { db } from "../../firebase/firebase.utils";
import uploadFile from "../../utils/uploadFile";
import AntEditor from "../editor/anteditor/anteditor";
import FrameSurface from "./frame-editor/FrameSurface";
import { frameCore as C } from "./frame-editor/frameCore";
import PostDetail from "./PostDetail";
import DropdownMenu from "../dropdown-menu/DropdownMenu";
import {
  FiX,
  FiPlus,
  FiMoreHorizontal,
  FiMove,
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiVideo,
  FiType,
  FiTrash2,
  FiCopy,
} from "react-icons/fi";
import { createDraftStore } from "./frame-editor/postDraftStore";
import "./post-create.styles.scss";

const TYPES = [
  "networking",
  "freelance",
  "productivity",
  "advice",
  "motivation",
  "stories",
];
const VISIBILITIES = ["public", "connections", "private"];
const draftKey = (uid) => "post_scene_draft_web_v1_" + uid;
const fresh = () => {
  const f = C.blank();
  return {
    version: C.VERSION,
    size: "tall",
    postId: db.collection("posts").doc().id,
    frames: [f],
    activeId: f.id,
    caption: "",
    tagsText: "",
    type: "productivity",
    visibility: "public",
  };
};

async function inspectMedia(file, kind) {
  if (file.size > (kind === "video" ? 50 : 12) * 1024 * 1024)
    throw new Error(
      kind === "video"
        ? "Videos must be under 50 MB."
        : "Images must be under 12 MB.",
    );
  if (
    kind === "image" &&
    !/^image\/(jpeg|png|webp|gif|avif|heic|heif)$/i.test(file.type)
  )
    throw new Error(
      "Choose a JPEG, PNG, WebP, GIF, AVIF, or supported HEIC image.",
    );
  if (kind === "video" && !file.type.startsWith("video/"))
    throw new Error("Choose a video file.");
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const node = document.createElement(kind === "video" ? "video" : "img");
      let timer;
      const finish = (error, result) => {
        clearTimeout(timer);
        node.onload = null;
        node.onerror = null;
        if (kind === "video") {
          node.onloadedmetadata = null;
          node.onseeked = null;
          node.removeAttribute("src");
          node.load();
        }
        error ? reject(error) : resolve(result);
      };
      timer = setTimeout(
        () =>
          finish(
            new Error(
              "This file could not be decoded. Try a different format.",
            ),
          ),
        15000,
      );
      node.onerror = () =>
        finish(new Error("This browser cannot read the file."));
      if (kind === "image")
        node.onload = () =>
          finish(null, {
            width: node.naturalWidth,
            height: node.naturalHeight,
          });
      else {
        node.preload = "auto";
        node.muted = true;
        node.playsInline = true;
        node.onloadedmetadata = () => {
          if (
            !Number.isFinite(node.duration) ||
            node.duration <= 0 ||
            node.duration > 60
          ) {
            finish(new Error("Choose a video up to 60 seconds long."));
            return;
          }
          node.currentTime = Math.min(0.25, node.duration / 2);
        };
        node.onseeked = () => {
          const durationMs = Math.round(node.duration * 1000),
            canvas = document.createElement("canvas");
          canvas.width = Math.min(720, node.videoWidth);
          canvas.height = Math.max(
            1,
            Math.round((node.videoHeight * canvas.width) / node.videoWidth),
          );
          canvas
            .getContext("2d")
            .drawImage(node, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) =>
              blob
                ? finish(null, {
                    durationMs,
                    cover: new File([blob], "cover.jpg", {
                      type: "image/jpeg",
                    }),
                  })
                : finish(new Error("Could not create a video cover.")),
            "image/jpeg",
            0.85,
          );
        };
      }
      node.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function PostCreate({
  modal = false,
  open = true,
  onClose,
  onPublished,
}) {
  const currentUser = useSelector((state) => state.user.currentUser),
    uid = currentUser?.uid;
  const [draft, setDraft] = useState(fresh),
    draftRef = useRef(draft);
  const [ready, setReady] = useState(false),
    [step, setStep] = useState(0),
    [visitedEditor, setVisitedEditor] = useState(false);
  const [busy, setBusy] = useState(""),
    busyRef = useRef(false),
    [error, setError] = useState(""),
    [status, setStatus] = useState("");
  const [agreed, setAgreed] = useState(false),
    [textEdit, setTextEdit] = useState(null),
    textRef = useRef("");
  const [editorImage, setEditorImage] = useState(null),
    [editorAlt, setEditorAlt] = useState("");
  const textHost = useRef(null),
    store = useRef(null);
  const [assets, setAssets] = useState({}),
    [surfaceReady, setSurfaceReady] = useState(false);
  const dragIndex = useRef(null);
  const refreshAssets = () => setAssets(store.current?.view() || {});
  useEffect(() => {
    if (!textEdit || !textHost.current) return;
    const root = textHost.current;
    const hydrate = () => store.current?.hydrateEditor(root);
    hydrate();
    const observer = new MutationObserver(hydrate);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src"],
    });
    return () => observer.disconnect();
  }, [textEdit]);
  const surface = useRef(null),
    input = useRef(null),
    assetRequest = useRef(null),
    saveTimer = useRef(null),
    published = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(saveTimer.current);
    };
  }, []);
  const sizeUndo = useRef(null);
  function change(updater) {
    const next =
      typeof updater === "function"
        ? updater(draftRef.current)
        : { ...draftRef.current, ...updater };
    if (
      sizeUndo.current &&
      JSON.stringify(next.frames) !== sizeUndo.current.after
    )
      sizeUndo.current = null;
    draftRef.current = next;
    setDraft(next);
  }
  function changeSize(key) {
    if (!ready || busyRef.current || key === draftRef.current.size) return;
    // Repeated choices use the same starting layout, so trying ratios is lossless.
    const current = draftRef.current;
    const before = sizeUndo.current || {
      frames: C.copy(current.frames),
      size: current.size,
    };
    const frames = before.frames.map((f) => C.resize(f, key));
    change({ size: key, frames });
    sizeUndo.current =
      key === before.size ? null : { ...before, after: JSON.stringify(frames) };
    setSurfaceReady(false);
  }
  function undoSize() {
    const previous = sizeUndo.current;
    if (!previous || busyRef.current) return;
    const changedSize = previous.size !== draftRef.current.size;
    change({ size: previous.size, frames: previous.frames });
    sizeUndo.current = null;
    if (changedSize) setSurfaceReady(false);
  }
  function updateFrame(frame) {
    change((old) => ({
      ...old,
      frames: old.frames.map((f) =>
        f.id === frame.id ? C.normalize(frame) : f,
      ),
    }));
  }
  function alterFrame(frameId, updater) {
    const old = draftRef.current.frames.find((f) => f.id === frameId);
    if (!old) throw new Error("That item has been removed.");
    const next = C.copy(old);
    updater(next);
    updateFrame(next);
  }
  function save(snapshot = draftRef.current) {
    if (published.current) return;
    localStorage.setItem(
      draftKey(uid || "anonymous"),
      JSON.stringify(snapshot),
    );
    setStatus("Draft saved on this browser");
  }
  useEffect(() => {
    let cancelled = false,
      localStore;
    published.current = false;
    setReady(false);
    sizeUndo.current = null;
    setStep(0);
    setVisitedEditor(false);
    setSurfaceReady(false);
    (async () => {
      let next = fresh();
      const raw = localStorage.getItem(draftKey(uid || "anonymous"));
      if (raw) {
        next = C.restore(JSON.parse(raw));
        next.postId ||= db.collection("posts").doc().id;
      }
      localStore = createDraftStore(uid, next.postId);
      const restoredAssets = await localStore.load();
      if (cancelled) {
        localStore.dispose();
        return;
      }
      store.current = localStore;
      setAssets(restoredAssets);
      change(next);
      setStatus(raw ? "Draft restored" : "");
      setReady(true);
    })().catch((e) => {
      if (!cancelled) setError("Could not restore the draft: " + e.message);
    });
    return () => {
      cancelled = true;
      localStore?.dispose();
    };
  }, [uid]);
  useEffect(() => {
    if (!ready || published.current || !open) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        save();
      } catch {
        setStatus("Draft could not be saved. Browser storage may be full.");
      }
    }, 500);
    return () => clearTimeout(saveTimer.current);
  }, [draft, ready, uid, open]);
  useEffect(() => {
    const write = () => {
      try {
        save();
      } catch {
        /* Cannot show a dialog during page shutdown. */
      }
    };
    window.addEventListener("pagehide", write);
    return () => window.removeEventListener("pagehide", write);
  }, [uid]);
  async function flush() {
    if (step === 1 && visitedEditor && surfaceReady && surface.current)
      try {
        await surface.current.flush();
      } catch (error) {
        await save();
        throw error;
      }
  }
  async function go(nextStep) {
    if (busyRef.current) return;
    try {
      await flush();
      if (nextStep === 1) setVisitedEditor(true);
      setStep(nextStep);
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }
  async function close() {
    if (busyRef.current) return;
    try {
      await flush();
      save();
      onClose?.();
    } catch (e) {
      setError(e.message);
    }
  }
  async function frameAction(action, index) {
    if (busyRef.current) return;
    try {
      await flush();
      const old = draftRef.current,
        frames = old.frames.slice();
      let activeId = Number.isInteger(index)
        ? frames[index]?.id || old.activeId
        : old.activeId;
      if (action === "select") activeId = frames[index].id;
      if (action === "add" || action === "duplicate") {
        if (frames.length >= C.MAX_FRAMES)
          throw new Error("You can add up to 10 items.");
        const frame =
          action === "add"
            ? C.blank(old.size)
            : C.duplicate(frames.find((f) => f.id === activeId));
        frames.push(frame);
        activeId = frame.id;
      }
      if (action === "delete") {
        if (!window.confirm("Remove this item from your post?")) return;
        const position = frames.findIndex((f) => f.id === activeId);
        frames.splice(position, 1);
        if (!frames.length) frames.push(C.blank(old.size));
        activeId = frames[Math.min(position, frames.length - 1)].id;
      }
      if (action === "left" || action === "right") {
        const a = frames.findIndex((f) => f.id === activeId),
          b = a + (action === "left" ? -1 : 1);
        if (b >= 0 && b < frames.length)
          [frames[a], frames[b]] = [frames[b], frames[a]];
      }
      change({ frames, activeId });
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }
  function pickAsset(request) {
    if (busyRef.current) return;
    assetRequest.current = request;
    input.current.accept =
      request.kind === "video"
        ? "video/*"
        : "image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic";
    input.current.value = "";
    input.current.click();
  }
  async function upload(file, assetId = C.id()) {
    const extension = (
      file.name.match(/\.[a-z0-9]+$/i)?.[0] ||
      (file.type.startsWith("video/") ? ".mp4" : ".jpg")
    ).toLowerCase();
    const { url } = await uploadFile({
      file,
      fullPath:
        "posts/" +
        uid +
        "/" +
        draftRef.current.postId +
        "/assets/" +
        assetId +
        extension,
      userId: uid,
      parent: "posts/" + draftRef.current.postId,
      onProgress: ({ progress }) => {
        if (mounted.current)
          setBusy("Uploading " + Math.round((progress || 0) * 100) + "%");
      },
    });
    if (!C.url(url)) throw new Error("The upload did not return a usable URL.");
    return url;
  }
  async function receiveFile(event) {
    const file = event.target.files?.[0],
      request = assetRequest.current;
    if (!file || !request || busyRef.current) return;
    busyRef.current = true;
    setBusy("Checking media…");
    setError("");
    try {
      await flush();
      const info = await inspectMedia(file, request.kind),
        url = await store.current.add(file);
      const coverUrl = info.cover ? await store.current.add(info.cover) : null;
      refreshAssets();
      if (!mounted.current) return;
      alterFrame(request.frameId, (f) => {
        if (request.target === "background")
          f.background = {
            ...f.background,
            kind: request.kind,
            url,
            coverUrl,
            durationMs: info.durationMs || null,
            positionX: 0.5,
            positionY: 0.5,
            zoom: 1,
          };
        else {
          let element = f.elements.find((e) => e.id === request.elementId);
          if (!element) {
            if (f.elements.length >= C.MAX_ELEMENTS)
              throw new Error("This item already has 80 elements.");
            element = C.make("image", f);
            element.width = 700;
            element.height = Math.min(
              1400,
              Math.max(100, (700 * info.height) / info.width),
            );
            const fit = Math.min(
              1,
              (f.width * 0.8) / element.width,
              (f.height * 0.8) / element.height,
            );
            element.width *= fit;
            element.height *= fit;
            element.x = (f.width - element.width) / 2;
            element.y = (f.height - element.height) / 2;
            f.elements.push(element);
          }
          element.props = {
            ...element.props,
            url,
            fit: "contain",
            positionX: 0.5,
            positionY: 0.5,
            zoom: 1,
          };
        }
      });
    } catch (e) {
      if (mounted.current) setError(e.message);
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy("");
    }
  }
  async function editText(request) {
    textRef.current = request.element.props.html;
    const editorHTML = await store.current.htmlForEditor(
      request.element.props.html,
    );
    setTextEdit({ ...request, editorHTML });
  }
  function applyText() {
    textRef.current =
      textHost.current?.querySelector(".anteditor_inner")?.innerHTML ??
      textRef.current;
    try {
      alterFrame(textEdit.frameId, (f) => {
        const e = f.elements.find((v) => v.id === textEdit.element.id);
        if (e) e.props.html = store.current.fromEditorHTML(textRef.current);
      });
      setTextEdit(null);
    } catch (e) {
      setError(e.message);
    }
  }
  async function uploadTextImage() {
    if (busyRef.current) throw new Error("Another upload is in progress.");
    if (!editorImage) throw new Error("Choose an image first.");
    busyRef.current = true;
    setBusy("Preparing image…");
    try {
      await inspectMedia(editorImage, "image");
      const ref = await store.current.add(editorImage);
      refreshAssets();
      return store.current.htmlForEditor(ref);
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy("");
    }
  }
  async function discard() {
    if (!window.confirm("Discard this draft and start a new post?")) return;
    clearTimeout(saveTimer.current);
    await store.current?.clear();
    localStorage.removeItem(draftKey(uid || "anonymous"));
    const next = fresh();
    store.current = createDraftStore(uid, next.postId);
    await store.current.load();
    refreshAssets();
    change(next);
    setSurfaceReady(false);
    setStep(0);
    setVisitedEditor(false);
    setStatus("");
    setAgreed(false);
    setReady(true);
    setError("");
  }
  async function publish() {
    if (busyRef.current || !agreed) return;
    busyRef.current = true;
    setBusy("Publishing…");
    setError("");
    try {
      await flush();
      if (!uid) throw new Error("Sign in to publish your post.");
      const current = draftRef.current;
      await save();
      const frames = await store.current.prepare(current.frames, upload);
      const payload = C.payload(frames, current, uid);
      const timestamp = firebase.firestore.FieldValue.serverTimestamp();
      await db
        .collection("posts")
        .doc(current.postId)
        .set({ ...payload, createdAt: timestamp, updatedAt: timestamp });
      published.current = true;
      clearTimeout(saveTimer.current);
      try {
        localStorage.removeItem(draftKey(uid || "anonymous"));
        await store.current.clear();
      } catch {
        /* Publishing succeeded; a storage error must not cause a duplicate retry. */
      }
      onPublished?.(current.postId);
      if (onClose) onClose();
      else if (!onPublished && !onClose) {
        const next = fresh();
        store.current = createDraftStore(uid, next.postId);
        await store.current.load();
        refreshAssets();
        change(next);
        setStep(0);
        setVisitedEditor(false);
        setAgreed(false);
        setStatus("Post published");
        published.current = false;
      }
    } catch (e) {
      setError(
        e.message || "Could not publish. Your draft is still available.",
      );
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy("");
    }
  }
  const activeFrame =
    draft.frames.find((f) => f.id === draft.activeId) || draft.frames[0];
  const previewPost = useMemo(() => {
    try {
      return {
        id: draft.postId,
        ...C.preview(draft.frames, draft, uid || "preview"),
        createdAt: new Date(),
      };
    } catch {
      return {
        id: draft.postId,
        authorId: uid,
        media: [],
        text: draft.caption || "Add content to preview your post.",
        type: draft.type,
        visibility: draft.visibility,
      };
    }
  }, [draft, uid]);
  async function addItem(kind) {
    if (busyRef.current || !ready) return;
    if (!(
      draftRef.current.frames.length === 1 &&
      !C.hasContent(draftRef.current.frames[0])
    ))
      await frameAction("add");
    if (kind !== "blank")
      pickAsset({
        frameId: draftRef.current.activeId,
        target: "background",
        kind,
      });
  }
  function reorder(from, to) {
    if (
      busyRef.current ||
      !Number.isInteger(from) ||
      from < 0 ||
      from >= draftRef.current.frames.length ||
      from === to ||
      to < 0 ||
      to >= draftRef.current.frames.length
    )
      return;
    change((old) => {
      const frames = old.frames.slice();
      frames.splice(to, 0, frames.splice(from, 1)[0]);
      return { ...old, frames };
    });
  }
  const activeIndex = draft.frames.findIndex((f) => f.id === activeFrame.id);
  const label = (value) => value.charAt(0).toUpperCase() + value.slice(1);
  const menuProps = {
    style: {
      "--dm-z-index": 2147483450,
      "--dm-accent": "var(--brand)",
      "--dm-text": "var(--primary)",
    },
    itemHeight: 38,
  };
  if (!open) return null;
  const content = (
    <section
      className={"post-create object-post-create" + (modal ? " is-modal" : "")}
      aria-label="Create a post"
    >
      <header className="object-post-create__header">
        <div>
          <strong>Create a post</strong>
          <small role="status">{busy || status || "Make it yours"}</small>
        </div>
        {onClose && (
          <button
            className="icon-button"
            disabled={!!busy}
            onClick={close}
            aria-label="Close editor"
          >
            <FiX />
          </button>
        )}
      </header>
      <nav className="object-post-create__steps" aria-label="Post creation">
        {["Details", "Edit", "Preview"].map((name, i) => (
          <button
            key={name}
            aria-current={step === i ? "step" : undefined}
            disabled={!ready || !!busy}
            onClick={() => go(i)}
          >
            {name}
          </button>
        ))}
      </nav>
      {!!error && (
        <div className="object-post-create__error" role="alert">
          <span>{error}</span>
          <button
            className="icon-button"
            aria-label="Dismiss error"
            onClick={() => setError("")}
          >
            <FiX />
          </button>
        </div>
      )}
      <div className="object-post-create__body">
        {step === 0 && (
          <div className="object-post-create__details">
            <section className="post-content-picker">
              <div className="post-section-title">
                <strong>Your post</strong>
                <DropdownMenu
                  {...menuProps}
                  triggerLabel={"Post size · " + C.size(draft.size).label}
                  buttonTitle="Post size"
                  selectedKey={draft.size}
                  disabled={!ready || !!busy}
                  triggerClassName="post-size-trigger"
                  items={[
                    ...C.SIZES.map((size) => ({
                      key: size.key,
                      label: size.label,
                      icon: (
                        <span
                          className="post-size-icon"
                          style={{
                            width: 22 * Math.min(1, size.width / size.height),
                            height: 22 * Math.min(1, size.height / size.width),
                          }}
                        />
                      ),
                      onPress: () => changeSize(size.key),
                    })),
                    ...(sizeUndo.current
                      ? [
                          {
                            key: "undo-size",
                            label: "Undo size change",
                            onPress: undoSize,
                          },
                        ]
                      : []),
                  ]}
                />
                <span>{draft.frames.length} / 10</span>
              </div>
              <div
                className="post-content-picker__items"
                style={{
                  "--post-ratio":
                    C.size(draft.size).width / C.size(draft.size).height,
                }}
              >
                {draft.frames.map((f, i) => {
                  const image =
                    f.background.coverUrl ||
                    (f.background.kind === "image" ? f.background.url : null) ||
                    f.elements.find((e) => e.type === "image")?.props.url;
                  const url = C.previewURL({ kind: "image", url: image }, assets);
                  const text = C.plain(
                    f.elements
                      .filter((e) => e.type === "text")
                      .map((e) => e.props.html)
                      .join(" "),
                  ).slice(0, 70);
                  return (
                    <article
                      key={f.id}
                      className={
                        "post-thumbnail" +
                        (f.id === draft.activeId ? " is-active" : "")
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        reorder(dragIndex.current, i);
                        dragIndex.current = null;
                      }}
                    >
                      <button
                        className="post-thumbnail__cover"
                        aria-label={"Edit item " + (i + 1)}
                        style={{ background: f.background.color }}
                        disabled={!!busy || !ready}
                        onClick={async () => {
                          await frameAction("select", i);
                          go(1);
                        }}
                      >
                        {url ? (
                          <img src={url} alt="" />
                        ) : text ? (
                          <span>{text}</span>
                        ) : (
                          <FiType />
                        )}
                        <small>{i + 1}</small>
                      </button>
                      <div className="post-thumbnail__tools">
                        <button
                          className="icon-button post-thumbnail__drag"
                          draggable={!busy}
                          title="Drag to reorder"
                          aria-label={"Reorder item " + (i + 1)}
                          onDragStart={(e) => {
                            dragIndex.current = i;
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", String(i));
                          }}
                          onDragEnd={() => {
                            dragIndex.current = null;
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === "ArrowLeft" ||
                              e.key === "ArrowRight"
                            ) {
                              e.preventDefault();
                              reorder(i, i + (e.key === "ArrowLeft" ? -1 : 1));
                            }
                          }}
                        >
                          <FiMove />
                        </button>
                        <DropdownMenu
                          {...menuProps}
                          triggerLabel={"Item " + (i + 1) + " options"}
                          customBtn={<FiMoreHorizontal />}
                          hasCaret={false}
                          disabled={!!busy}
                          triggerClassName="post-thumbnail__menu"
                          items={[
                            {
                              key: "edit",
                              label: "Edit",
                              onPress: async () => {
                                await frameAction("select", i);
                                go(1);
                              },
                            },
                            {
                              key: "left",
                              label: "Move earlier",
                              disabled: i === 0,
                              onPress: () => reorder(i, i - 1),
                            },
                            {
                              key: "right",
                              label: "Move later",
                              disabled: i === draft.frames.length - 1,
                              onPress: () => reorder(i, i + 1),
                            },
                            {
                              key: "copy",
                              label: "Duplicate",
                              icon: <FiCopy />,
                              disabled: draft.frames.length >= 10,
                              onPress: () => frameAction("duplicate", i),
                            },
                            {
                              key: "delete",
                              label: "Remove",
                              icon: <FiTrash2 />,
                              onPress: () => frameAction("delete", i),
                            },
                          ]}
                        />
                      </div>
                    </article>
                  );
                })}
                <DropdownMenu
                  {...menuProps}
                  triggerLabel="Add content"
                  customBtn={
                    <>
                      <FiPlus />
                      <span>Add</span>
                    </>
                  }
                  hasCaret={false}
                  disabled={!ready || !!busy || draft.frames.length >= 10}
                  triggerClassName="post-add-tile"
                  items={[
                    {
                      key: "photo",
                      label: "Photo",
                      icon: <FiImage />,
                      onPress: () => addItem("image"),
                    },
                    {
                      key: "video",
                      label: "Video",
                      icon: <FiVideo />,
                      onPress: () => addItem("video"),
                    },
                    {
                      key: "blank",
                      label: "Text or blank",
                      icon: <FiType />,
                      onPress: () => addItem("blank"),
                    },
                  ]}
                />
              </div>
              <p className="post-hint">
                Tap an item to edit. Drag its handle to change the order.
              </p>
            </section>
            <label className="wide">
              Caption
              <textarea
                aria-label="Caption"
                rows={4}
                maxLength={5000}
                value={draft.caption}
                placeholder="What would you like to share?"
                onChange={(e) => change({ caption: e.target.value })}
              />
            </label>
            <div className="post-field">
              <span>Category</span>
              <DropdownMenu
                {...menuProps}
                triggerLabel={label(draft.type)}
                selectedKey={draft.type}
                items={TYPES.map((key) => ({ key, label: label(key) }))}
                onSelect={(type) => change({ type })}
              />
            </div>
            <div className="post-field">
              <span>Who can see this?</span>
              <DropdownMenu
                {...menuProps}
                triggerLabel={label(draft.visibility)}
                selectedKey={draft.visibility}
                items={VISIBILITIES.map((key) => ({ key, label: label(key) }))}
                onSelect={(visibility) => change({ visibility })}
              />
            </div>
            <label className="wide">
              Tags
              <input
                value={draft.tagsText}
                placeholder="design, productivity"
                onChange={(e) => change({ tagsText: e.target.value })}
              />
            </label>
            <button
              className="post-discard"
              disabled={!!busy}
              onClick={discard}
            >
              Discard draft
            </button>
          </div>
        )}
        {visitedEditor && (
          <div
            className="object-post-create__editor"
            style={{ display: step === 1 ? "flex" : "none" }}
          >
            <div className="object-post-create__surface">
              <FrameSurface
                key={draft.size}
                navigation={
                  <div className="post-item-navigation">
                    <button
                      className="icon-button"
                      aria-label="Previous item"
                      disabled={activeIndex === 0 || !!busy}
                      onClick={() => frameAction("select", activeIndex - 1)}
                    >
                      <FiChevronLeft />
                    </button>
                    <span>
                      {activeIndex + 1} / {draft.frames.length}
                    </span>
                    <button
                      className="icon-button"
                      aria-label="Next item"
                      disabled={
                        activeIndex === draft.frames.length - 1 || !!busy
                      }
                      onClick={() => frameAction("select", activeIndex + 1)}
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                }
                ref={surface}
                frame={activeFrame}
                assets={assets}
                editable
                active={step === 1 && !textEdit}
                disabled={!!busy || !!textEdit}
                onReady={() => setSurfaceReady(true)}
                onChange={updateFrame}
                onEditText={(request) =>
                  editText(request).catch((e) => setError(e.message))
                }
                onPickAsset={pickAsset}
                onError={setError}
              />
            </div>
          </div>
        )}
        {step === 2 && (
          <div
            className="object-post-create__preview"
            style={{
              "--post-ratio":
                C.size(draft.size).width / C.size(draft.size).height,
            }}
          >
            <PostDetail
              postId={draft.postId}
              initialPost={previewPost}
              initialAuthor={currentUser}
              preview
              active
              draftAssets={assets}
            />
          </div>
        )}
      </div>
      <footer className="object-post-create__footer">
        {step === 2 && (
          <label>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            I agree to the community rules
          </label>
        )}
        {step > 0 && (
          <button disabled={!!busy} onClick={() => go(step - 1)}>
            Back
          </button>
        )}
        {step < 2 ? (
          <button
            className="primary"
            disabled={!ready || !!busy}
            onClick={() => go(step + 1)}
          >
            Continue <FiChevronRight />
          </button>
        ) : (
          <button
            className="primary"
            disabled={!agreed || !!busy}
            onClick={publish}
          >
            Publish
          </button>
        )}
      </footer>
      <input ref={input} type="file" hidden onChange={receiveFile} />
      {textEdit && (
        <>
          <div className="post-text-backdrop" />
          <div
            className="post-text-dialog"
            style={{
              "--post-text-width":
                Math.max(
                  360,
                  Math.min(760, (textEdit.displayWidth || 520) + 40),
                ) + "px",
              fontFamily: C.fontFamily(textEdit.element.props.fontFamily),
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Edit text"
            onKeyDown={(e) => {
              if (e.key === "Escape" && !busy) setTextEdit(null);
            }}
          >
            <header>
              <strong>Edit text</strong>
              <button disabled={!!busy} onClick={() => setTextEdit(null)}>
                Cancel
              </button>
              <button className="primary" disabled={!!busy} onClick={applyText}>
                Done
              </button>
            </header>
            <div className="post-text-dialog__editor" ref={textHost}>
              <AntEditor
                key={textEdit.element.id}
                mainToolbarOn
                floatingToolbarOn={false}
                editability
                initialValue={textEdit.editorHTML}
                placeholder="Write your text…"
                setDocContent={(html) => {
                  textRef.current = html;
                }}
                setDocImgFile={setEditorImage}
                setDocImgFileAltText={setEditorAlt}
                docImgFileAltText={editorAlt}
                handleUploadDocImage={uploadTextImage}
              />
            </div>
          </div>
        </>
      )}
      {!!busy && (
        <div className="object-post-create__busy" role="status">
          {busy}
        </div>
      )}
    </section>
  );
  return modal
    ? createPortal(
        <div className="object-post-create__modal">{content}</div>,
        document.body,
      )
    : content;
}
